import { createApi, fetchBaseQuery, BaseQueryFn } from '@reduxjs/toolkit/query/react';
import { getApiConfig } from '@/config/api';
import { getRequestLanguage } from '@/utils/requestLanguage';
import type { Subcategory, ApiResponse } from '@/types';

// Create a dynamic base query for subcategory API
const dynamicSubcategoryBaseQuery: BaseQueryFn = async (args, api, extraOptions) => {
  const config = getApiConfig();

  const baseQuery = fetchBaseQuery({
    baseUrl: config.API_URL,
    prepareHeaders: (headers, { getState }) => {
      let token = (getState() as any).auth.token;
      if (!token) {
        token = localStorage.getItem('token');
      }
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      headers.set('accept-language', getRequestLanguage());
      return headers;
    },
  });

  return baseQuery(args, api, extraOptions);
};

export const subcategoryApi = createApi({
  reducerPath: 'subcategoryApi',
  baseQuery: dynamicSubcategoryBaseQuery,
  tagTypes: ['Subcategory', 'Category'],
  endpoints: (builder) => ({
    // Get all subcategories
    getSubcategories: builder.query<ApiResponse<Subcategory[]>, { storeId?: string; categoryId?: string; includeInactive?: boolean }>({
      query: (params) => ({
        url: '/categories/subcategories/all',
        params,
      }),
      providesTags: ['Subcategory'],
    }),

    // Get subcategories by category
    getSubcategoriesByCategory: builder.query<ApiResponse<Subcategory[]>, string>({
      query: (categoryId) => ({
        url: '/categories/subcategories/all',
        params: { categoryId },
      }),
      providesTags: (_, __, categoryId) => [{ type: 'Subcategory', id: categoryId }],
    }),

    // Get single subcategory
    getSubcategory: builder.query<ApiResponse<Subcategory>, string>({
      query: (id) => `/categories/subcategories/${id}`,
      providesTags: (_, __, id) => [{ type: 'Subcategory', id }],
    }),

    // Create subcategory
    createSubcategory: builder.mutation<
      ApiResponse<Subcategory>,
      { categoryId: string; name: string; description?: string; isActive?: boolean }
    >({
      query: (data) => ({
        url: '/categories/subcategories',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Subcategory', 'Category'],
    }),

    // Update subcategory
    updateSubcategory: builder.mutation<
      ApiResponse<Subcategory>,
      { id: string; data: { name?: string; description?: string; isActive?: boolean } }
    >({
      query: ({ id, data }) => ({
        url: `/categories/subcategories/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_, __, { id }) => [{ type: 'Subcategory', id }, 'Category'],
    }),

    // Delete subcategory
    deleteSubcategory: builder.mutation<ApiResponse<void>, string>({
      query: (id) => ({
        url: `/categories/subcategories/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Subcategory', 'Category'],
    }),
  }),
});

export const {
  useGetSubcategoriesQuery,
  useGetSubcategoriesByCategoryQuery,
  useGetSubcategoryQuery,
  useCreateSubcategoryMutation,
  useUpdateSubcategoryMutation,
  useDeleteSubcategoryMutation,
} = subcategoryApi;
